/**
 * Notion — the second reference semantic connector profile.
 *
 * The point of this profile is to prove the abstraction generalizes: "Notion
 * is Miro minus canvas plus tree" (03-PROFILE §8). Where Miro speaks
 * stickies-frames-connectors on a canvas, Notion speaks pages-databases-
 * relations in a tree. The SAME machinery — a profile + default maps + a pure
 * snapshot builder — adds it, with no new architecture.
 *
 * The high-value gap this closes: the flat Notion import
 * (notionService.importFromDatabase) DROPS relation properties entirely, so
 * cross-references between Notion pages never become canonical edges. Notion's
 * `relation` properties are its connectors; its page tree is its containment;
 * its select/status option colors are its visual language.
 */

import type { ConnectorProfile } from '../profileTypes';
import type { RelationshipCueRule, TypeMapRule } from '../../fieldMappingRegistry';

export const NOTION_PROFILE: ConnectorProfile = {
  connectorType: 'notion',
  profileVersion: 1,

  nativeObjectTypes: ['database', 'page', 'sub_page', 'relation', 'select', 'status', 'multi_select'],
  // Notion expresses relationships two ways: typed `relation` properties
  // (references) and the page/sub-page tree (containment).
  relationshipModel: 'references',
  // Select/status option colors are the only reliable visual channel; there is
  // no free fill color per page the way a Miro sticky has one.
  visualChannels: ['fill_color'],
  spatialModel: 'tree',
  acquisition: 'api',

  fidelity: {
    content: {
      export: 'full',
      import: 'full',
      notes: 'Page title + rich-text properties import fully; page body blocks import as description text (one level deep, per notionService.getPageText).',
    },
    structure: {
      export: 'partial',
      import: 'partial',
      notes: 'Database membership and the page/sub-page tree survive as Collection membership and `contains` edges. Arbitrary block nesting below the first level is flattened.',
    },
    relationships: {
      export: 'none',
      import: 'partial',
      notes: 'Notion `relation` properties become canonical edges on import (the property NAME is the cue — "Blocks"/"Depends on"/"Parent item" resolve to dependency/prerequisite/aggregation). Export writes properties, not relations, so relationships do not travel outbound yet.',
    },
    semantics: {
      export: 'partial',
      import: 'partial',
      notes: 'Select/status option colors map to item category via the workspace visual rules (a red "Risk" status → risk), exactly like Miro fill colors. Meaning in an unmapped property does not survive.',
    },
  },

  identity: { stableExternalIds: true, deepLinks: true, appMetadata: false },
  continuity: { webhooks: true, comments: false },
};

// ── Profile-shipped default maps (workspace v3 rules override) ────────────────

/** Native type routing defaults. */
export const NOTION_DEFAULT_TYPE_MAP: TypeMapRule[] = [
  { nativeType: 'page', target: 'item' },
  { nativeType: 'sub_page', target: 'item' },
  { nativeType: 'database', target: 'region' },
];

/**
 * Notion relation-property NAME → canonical relationship. The property name is
 * the cue (kind 'link_type'), resolved through the SAME precedence machinery
 * as Miro connector captions (resolveRelationshipCue). Names are matched
 * case-insensitively; a relation whose name matches nothing falls back to
 * `related` (see buildNotionSnapshot), never dropped.
 */
export const NOTION_DEFAULT_RELATIONSHIP_MAP: RelationshipCueRule[] = [
  { cue: { kind: 'link_type', value: 'blocks' }, canonicalType: 'dependency', subtype: 'hard', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'blocked by' }, canonicalType: 'prerequisite', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'depends on' }, canonicalType: 'dependency', subtype: 'hard', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'depends' }, canonicalType: 'dependency', subtype: 'hard', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'prerequisite' }, canonicalType: 'prerequisite', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'requires' }, canonicalType: 'prerequisite', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  // Notion's native hierarchy relations
  { cue: { kind: 'link_type', value: 'parent item' }, canonicalType: 'aggregation', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'sub-item' }, canonicalType: 'breakdown', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'sub-items' }, canonicalType: 'breakdown', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'elaborates' }, canonicalType: 'elaboration', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  { cue: { kind: 'link_type', value: 'part of' }, canonicalType: 'aggregation', directionality: 'directed', defaultConfidence: 1, basis: 'explicit' },
  // Page-tree containment (a sub-page under a page, not a database row)
  { cue: { kind: 'containment', value: 'page' }, canonicalType: 'contains', directionality: 'directed', defaultConfidence: 0.85, basis: 'visual_language' },
];

/**
 * A relation property whose name matches no rule still becomes an edge — a
 * generic `related` at reduced confidence — rather than being dropped (the
 * flat import's failure mode). It goes through the review path, not a silent
 * write, per the uncertainty policy.
 */
export const NOTION_UNNAMED_RELATION = {
  canonicalType: 'related' as const,
  directionality: 'undirected' as const,
  defaultConfidence: 0.6,
  basis: 'spatial' as const, // "spatial" here means structural inference → proposal, never direct
};

/** Description length beyond which import keeps the full page body (always, for Notion). */
export const NOTION_KEEPS_FULL_BODY = true;
